import React, { useState } from 'react';
import { PropTypes } from 'utils/wrappers';
import { Button, Input } from 'reactstrap';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import classes from 'components/Page/Property/styles/PaymentCalculator.module.scss';
import Price from 'components/Common/Property/Price';
import { showCheckoutModal, updateCheckoutModalTypeAction } from 'redux/actions/modalsActions';
import { connect } from 'react-redux';

const getMonthlyPayment = (amount, years, interest) => {
    const monthlyInterest = interest / 12;
    const term = years * 12;

    const monthlyPayment =
        Math.round(
            (amount / (((1 + monthlyInterest) ** term - 1) / (monthlyInterest * (1 + monthlyInterest) ** term))) * 100,
        ) / 100;

    return Math.max(monthlyPayment, 0);
};

const FinanceIt = ({ property, className, updateCheckoutModalType }) => {
    const minTerms = 1;
    const maxTerms = Math.min(property.finances.terms / 12, 20);

    const interest = property.finances.interestRate / 100;
    const total = property.finances.price;

    const processingFee = property.finances.processingFee;

    const defaultDownPayment = property.finances.minimumDownPayment
        ? property.finances.minimumDownPayment
        : Math.round(0.05 * total);

    const [term, setTerm] = useState(maxTerms);
    const [downPayment, setDownPayment] = useState(defaultDownPayment);

    const monthlyPayment = getMonthlyPayment(total - downPayment, term, interest);

    const dueToday = Math.min(downPayment + processingFee, total);

    const openCheckoutModal = () => {
        updateCheckoutModalType('finance', downPayment);

        showCheckoutModal();
    };

    return (
        <div className={`${classes.finance}`}>
            <h4 className={'font-weight-bold'}>Finance Terms</h4>
            <div className={'row mb-2'}>
                <div className={'col-7'}>Purchase Price</div>
                <div className={'col-5 text-right'}>
                    <Price value={total} />
                </div>
            </div>
            <div className={'row mb-2'}>
                <div className={'col-7'}>Interest Rate</div>
                <div className={'col-5 text-right'}>{interest * 100}%</div>
            </div>
            <div className={'row mb-2'}>
                <div className={'col-7'}>Down Payment</div>
                <div className={`col-5 text-right position-relative ${classes['down-payment']}`}>
                    <Input
                        value={downPayment}
                        className={'pr-4 pt-1 pb-1 h-100 text-right'}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                setDownPayment('');
                            }

                            if (e.currentTarget.value !== `${parseInt(e.target.value)}`) {
                                return;
                            }

                            setDownPayment(parseInt(e.currentTarget.value));
                        }}
                        onBlur={(e) => {
                            if (parseInt(e.target.value) > total) {
                                return setDownPayment(total);
                            }

                            if (parseInt(e.target.value) < defaultDownPayment) {
                                return setDownPayment(defaultDownPayment);
                            }
                        }}
                    />
                    <span className={`position-absolute mr-4 ${classes['dollar-sign']}`}>$</span>
                </div>
            </div>
            <div className={'row mb-2'}>
                <div className={'col-7'}>Loan Terms</div>
                <div className={'col-5 text-right'}>
                    {term} {term > 1 ? 'Years' : 'Year'}
                </div>
            </div>
            {maxTerms > 1 ? (
                <div className={'row mb-2'}>
                    <div className={'col'}>
                        <Slider
                            min={minTerms}
                            max={maxTerms}
                            value={term}
                            step={1}
                            onChange={(value) => setTerm(value)}
                        />
                    </div>
                </div>
            ) : null}

            <h4 className={'font-weight-bold'}>Monthly Payments</h4>
            <div className={'row mb-2'}>
                <div className={'col-7'}>Monthly Payments</div>
                <div className={'col-5 text-right'}>
                    <Price value={monthlyPayment} withFraction={true} />
                </div>
            </div>
            <h4 className={'font-weight-bold'}>Due Today</h4>
            <div className={'row mb-2'}>
                <div className={'col-7'}>Down Payment</div>
                <div className={'col-5 text-right'}>
                    <Price value={downPayment} />
                </div>
            </div>
            <div className={'row mb-2'}>
                <div className={'col-7'}>Processing Fee</div>
                <div className={'col-5 text-right'}>
                    <Price value={processingFee} />
                </div>
            </div>
            <div className={'row mb-2 font-weight-bold'}>
                <div className={'col-7'}>Total Due Today</div>
                <div className={'col-5 text-right'}>
                    <Price value={dueToday} />
                </div>
            </div>
            <div className={classes['buy-button']}>
                <Button color='primary' size='lg' className={classes.button} onClick={openCheckoutModal}>
                    Buy it now
                </Button>
            </div>
        </div>
    );
};

FinanceIt.propTypes = {
    property: PropTypes.shape({
        listingId: PropTypes.string.isRequired,
        financialTerm: PropTypes.string,
        finances: PropTypes.shape({
            price: PropTypes.number.isRequired,
            monthlyPayment: PropTypes.number.isRequired,
            processingFee: PropTypes.number.isRequired,
            interestRate: PropTypes.number.isRequired,
            terms: PropTypes.number.isRequired,
        }).isRequired,
    }).isRequired,
    className: PropTypes.string,
};

const mapDispatchToProps = {
    updateCheckoutModalType: updateCheckoutModalTypeAction,
};

export default connect(null, mapDispatchToProps)(FinanceIt);
